import { useLazyQuery, useMutation } from "@apollo/client";
import { clientNeoFact } from "@neonumy/api-clients";
import { useMemo, useState, useEffect } from "react";
import {
  DELETE_PAYMENT,
  LIST_DIRECT_DEBITS,
  CREATE_DIRECT_DEBIT,
  UPDATE_DIRECT_DEBIT,
  CANCEL_DIRECT_DEBIT,
  DELETE_DIRECT_DEBIT,
  VALIDATE_DIRECT_DEBIT,
  LIST_INVOICES_FOR_DIRECT_DEBITS,
  LIST_INVOICES_OF_DIRECTS_DEBITS,
  LIST_INVOICES_OF_DIRECTS_DEBITS_IDS,
  LIST_INVOICES_FOR_DIRECT_DEBITS_IDS,
} from "queries/centralApi/payments";
import { DIRECT_DEBIT_STATUS_CHOICES } from "queries/centralApi/enums";
import { useTranslation } from "react-i18next";
import { onErrorGraphql } from "utils/onErrorGraphql";

export default function useDirectDebitApi(id?: ID) {
  const { t } = useTranslation();

  const [
    listDirectDebitSaleDocuments,
    {
      refetch: refetchListDirectDebitInvoices,
      loading: loadingListDirectDebitInvoices,
      data: listDirectDebitSaleDocumentsData,
      error: listDirectDebitSaleDocumentsError,
    },
  ] = useLazyQuery<ListDirectDebitsInvoicesResponse, ListDirectDebitInvoicesVars>(
    LIST_INVOICES_FOR_DIRECT_DEBITS,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (listDirectDebitSaleDocumentsError) onErrorGraphql(listDirectDebitSaleDocumentsError);
  }, [listDirectDebitSaleDocumentsError]);

  const initialListInvoicesData: ListDirectDebitInvoicesResults =
    listDirectDebitSaleDocumentsData?.listDirectDebitSaleDocuments?.results?.map(item => ({
      ...item,
      key: item?.id,
    })) ?? [];

  const [listInvoicesData, setListInvoicesData] =
    useState<ListDirectDebitInvoicesResults>(initialListInvoicesData);

  useMemo(() => {
    if (listDirectDebitSaleDocumentsData) {
      setListInvoicesData(initialListInvoicesData);
    }
  }, [listDirectDebitSaleDocumentsData]);

  const ddSaleDocsCount =
    listDirectDebitSaleDocumentsData?.listDirectDebitSaleDocuments?.totalCount ?? 0;

  const [getDirectDebitSaleDocumentsIds, { error: getDirectDebitSaleDocumentsIdsError }] =
    useLazyQuery<ListDirectDebitSaleDocumentsIdsResponse, ListDirectDebitSaleDocumentsIdsVars>(
      LIST_INVOICES_FOR_DIRECT_DEBITS_IDS,
      {
        client: clientNeoFact,
        fetchPolicy: "no-cache",
      },
    );

  useEffect(() => {
    if (getDirectDebitSaleDocumentsIdsError) onErrorGraphql(getDirectDebitSaleDocumentsIdsError);
  }, [getDirectDebitSaleDocumentsIdsError]);

  const [
    listDirectDebits,
    {
      refetch: refetchListDirectDebits,
      loading: loadinglistDirectDebits,
      data: listDirectDebitsData,
      error: listDirectDebitsError,
    },
  ] = useLazyQuery<ListDirectDebitsResponse, ListDirectDebitsVars>(LIST_DIRECT_DEBITS, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listDirectDebitsError) onErrorGraphql(listDirectDebitsError);
  }, [listDirectDebitsError]);

  const directDebitsData: ListDirectDebitsResults =
    listDirectDebitsData?.listDirectDebits?.results?.map(item => ({
      ...item,
      key: item.id,
    })) ?? [];

  const totalCount = listDirectDebitsData?.listDirectDebits?.totalCount ?? 0;

  const [
    listSaleDocumentsOfDirectDebits,
    {
      refetch: refetchDirectDebits,
      loading: loadingDirectDebits,
      data: listSaleDocumentsOfDirectDebitsData,
      error: listSaleDocumentsOfDirectDebitsError,
    },
  ] = useLazyQuery<ListInvoicesOfDirectDebitsResponse, ListInvoicesOfDirectDebitsVars>(
    LIST_INVOICES_OF_DIRECTS_DEBITS,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  useEffect(() => {
    if (listSaleDocumentsOfDirectDebitsError) onErrorGraphql(listSaleDocumentsOfDirectDebitsError);
  }, [listSaleDocumentsOfDirectDebitsError]);

  const listInvoicesOfDirectDebitsData: ListInvoicesOfDirectDebitsResults =
    listSaleDocumentsOfDirectDebitsData?.listSaleDocumentsOfDirectDebits?.results?.map(item => ({
      ...item,
      key: item.id,
    })) ?? [];

  const directDebitsTotalCount =
    listSaleDocumentsOfDirectDebitsData?.listSaleDocumentsOfDirectDebits?.totalCount ?? 0;

  const [getAllDirectDebitsIds, { error: getAllDirectDebitsIdsError }] = useLazyQuery<
    ListSaleDocumentsOfDirectDebitsIdsResponse,
    ListSaleDocumentsOfDirectDebitsIdsVars
  >(LIST_INVOICES_OF_DIRECTS_DEBITS_IDS, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getAllDirectDebitsIdsError) onErrorGraphql(getAllDirectDebitsIdsError);
  }, [getAllDirectDebitsIdsError]);

  const [createUpdateDirectDebit, { loading: createUpdateDirectDebitLoading }] = useMutation<
    CreateUpdateDirectDebitMutationResponse,
    CreateUpdateDirectDebitVars
  >(id ? UPDATE_DIRECT_DEBIT : CREATE_DIRECT_DEBIT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [deletePayment, { loading: deletePaymentLoading }] = useMutation<
    DeletePaymentResponse,
    GetVarTypeID
  >(DELETE_PAYMENT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [cancelDirectDebit] = useMutation<CancelDirectDebitResponse, GetVarTypeID>(
    CANCEL_DIRECT_DEBIT,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  const [validateDirectDebit] = useMutation<ValidateDirectDebitResponse, GetVarTypeID>(
    VALIDATE_DIRECT_DEBIT,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  const [deleteDirectDebit] = useMutation<DeleteDirectDebitResponse, GetVarTypeID>(
    DELETE_DIRECT_DEBIT,
    {
      client: clientNeoFact,
      fetchPolicy: "no-cache",
    },
  );

  const [
    listDirectDebitStatus,
    { data: listDirectDebitStatusData, error: listDirectDebitStatusError },
  ] = useLazyQuery<DirectDebitStatusChoicesResponse>(DIRECT_DEBIT_STATUS_CHOICES, {
    client: clientNeoFact,
  });

  useEffect(() => {
    if (listDirectDebitStatusError) onErrorGraphql(listDirectDebitStatusError);
  }, [listDirectDebitStatusError]);

  const directDebitsOptions = useMemo(
    () =>
      listDirectDebitStatusData?.listDirectDebitStatus?.map(item => ({
        label: t(item.label),
        value: item.value,
      })) ?? [],
    [listDirectDebitStatusData, t],
  );

  return {
    totalCount,
    deletePayment,
    ddSaleDocsCount,
    listDirectDebits,
    listInvoicesData,
    directDebitsData,
    cancelDirectDebit,
    deleteDirectDebit,
    loadingDirectDebits,
    refetchDirectDebits,
    validateDirectDebit,
    setListInvoicesData,
    directDebitsOptions,
    deletePaymentLoading,
    listDirectDebitStatus,
    getAllDirectDebitsIds,
    directDebitsTotalCount,
    loadinglistDirectDebits,
    createUpdateDirectDebit,
    refetchListDirectDebits,
    listDirectDebitSaleDocuments,
    getDirectDebitSaleDocumentsIds,
    refetchListDirectDebitInvoices,
    loadingListDirectDebitInvoices,
    listInvoicesOfDirectDebitsData,
    createUpdateDirectDebitLoading,
    listSaleDocumentsOfDirectDebits,
  };
}
